import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCalendar, FiUser, FiMail, FiPhone, FiBriefcase, FiMessageSquare, FiSend, FiCheck, FiAlertCircle, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import './ContactModal.css';

const ContactModal = ({ isOpen, onClose, serviceContext = '', buttonContext = '' }) => {
  console.log('ContactModal rendered:', { isOpen, serviceContext, buttonContext });
  console.log('REACT_APP_API_URL', process.env.REACT_APP_API_URL);

  // Generate available time slots (business hours: 9 AM - 5 PM, 15-minute intervals)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 9; hour < 17; hour++) {
      for (let minute of [0, 15, 30, 45]) {
        const timeValue = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        const displayTime = new Date(`2000-01-01T${timeValue}`).toLocaleTimeString([], { 
          hour: 'numeric', 
          minute: '2-digit',
          hour12: true 
        });
        slots.push({ value: timeValue, display: displayTime });
      }
    }
    return slots;
  };

  // Generate next 14 days (excluding weekends for business scheduling)
  const generateAvailableDays = () => {
    const days = [];
    const today = new Date();
    let currentDate = new Date(today);
    currentDate.setDate(currentDate.getDate() + 1); // Start from tomorrow
    
    while (days.length < 10) { // Show 10 business days
      const dayOfWeek = currentDate.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Exclude Sunday (0) and Saturday (6)
        days.push({
          date: new Date(currentDate),
          dateString: currentDate.toISOString().split('T')[0],
          displayDate: currentDate.toLocaleDateString('en-US', { 
            weekday: 'short', 
            month: 'short', 
            day: 'numeric' 
          })
        });
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return days;
  };

  const timeSlots = generateTimeSlots();
  const availableDays = generateAvailableDays();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(0);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: serviceContext,
    message: ''
  });

  const [formState, setFormState] = useState({
    isSubmitting: false,
    isSubmitted: false,
    error: null
  });

  const [validationErrors, setValidationErrors] = useState({});

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim() || formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters long';
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim() || !emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    if (!formData.phone.trim() || !phoneRegex.test(formData.phone.replace(/[\s\-\(\)]/g, ''))) {
      errors.phone = 'Please enter a valid phone number';
    }
    
    if (!formData.message.trim() || formData.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters long';
    }

    if (!selectedDate || !selectedTime) {
      errors.datetime = 'Please select a preferred date and time';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleDateSelect = (day) => {
    setSelectedDate(day);
    setSelectedTime(null);
    
    // Clear datetime validation error
    if (validationErrors.datetime) {
      setValidationErrors(prev => ({
        ...prev,
        datetime: ''
      }));
    }
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    
    // Clear datetime validation error
    if (validationErrors.datetime) {
      setValidationErrors(prev => ({
        ...prev,
        datetime: ''
      }));
    }
  };

  const getSelectedDateTime = () => {
    if (!selectedDate || !selectedTime) return 'Not specified';
    return `${selectedDate.displayDate} at ${selectedTime.display}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setFormState({
      isSubmitting: true,
      isSubmitted: false,
      error: null
    });

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      
      // Prepare enhanced message with context
      const enhancedMessage = `
${formData.message}

---
Inquiry Context: ${buttonContext || 'General Inquiry'}
Service Interest: ${formData.service || 'Not specified'}
Preferred Contact Time: ${getSelectedDateTime()}
Source: Website Contact Modal
      `.trim();

      const response = await fetch(`${apiUrl}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          company: formData.company.trim() || null,
          service: formData.service || null,
          message: enhancedMessage
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send message');
      }

      const result = await response.json();
      
      setFormState({
        isSubmitting: false,
        isSubmitted: true,
        error: null
      });

      // Auto-close after 3 seconds
      setTimeout(() => {
        handleClose();
      }, 3000);

    } catch (error) {
      console.error('Error submitting form:', error);
      setFormState({
        isSubmitting: false,
        isSubmitted: false,
        error: error.message || 'Failed to send message. Please try again.'
      });
    }
  };

  const handleClose = () => {
    if (!formState.isSubmitting) {
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: serviceContext,
        message: ''
      });
      setSelectedDate(null);
      setSelectedTime(null);
      setCurrentWeekStart(0);
      setFormState({
        isSubmitting: false,
        isSubmitted: false,
        error: null
      });
      setValidationErrors({});
      onClose();
    }
  };

  const getVisibleDays = () => {
    return availableDays.slice(currentWeekStart, currentWeekStart + 5);
  };

  const canGoNext = () => {
    return currentWeekStart + 5 < availableDays.length;
  };

  const canGoPrev = () => {
    return currentWeekStart > 0;
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { 
        duration: 0.3,
        ease: "easeOut"
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.8,
      transition: { 
        duration: 0.2,
        ease: "easeIn"
      }
    }
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div 
        className="contact-modal-overlay"
        variants={overlayVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={handleClose}
      >
        <motion.div 
          className="contact-modal calendly-style"
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="contact-modal-header">
            <div className="contact-modal-title">
              <h2>
                {formState.isSubmitted ? (
                  <>
                    <FiCheck className="success-icon" />
                    Message Sent Successfully!
                  </>
                ) : (
                  <>
                    <FiCalendar />
                    Schedule a Consultation
                  </>
                )}
              </h2>
              {!formState.isSubmitted && (
                <p className="contact-modal-subtitle">
                  {serviceContext 
                    ? `Interested in ${serviceContext}? Let's schedule a time to discuss how we can help.`
                    : "Let's schedule a time to discuss how we can help transform your business."
                  }
                </p>
              )}
            </div>
            <button 
              className="contact-modal-close"
              onClick={handleClose}
              disabled={formState.isSubmitting}
            >
              <FiX />
            </button>
          </div>

          {/* Content */}
          <div className="contact-modal-content">
            {formState.isSubmitted ? (
              <motion.div 
                className="success-message"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="success-icon-large">
                  <FiCheck />
                </div>
                <h3>Thank you for reaching out!</h3>
                <p>We've received your message and will get back to you within 24 hours.</p>
                <p className="auto-close-text">This window will close automatically...</p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="contact-modal-form">
                {/* Basic Info */}
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      <FiUser className="form-icon" />
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`form-input ${validationErrors.name ? 'error' : ''}`}
                      placeholder="Your full name"
                      disabled={formState.isSubmitting}
                      required
                    />
                    {validationErrors.name && (
                      <span className="error-message">
                        <FiAlertCircle />
                        {validationErrors.name}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <FiMail className="form-icon" />
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`form-input ${validationErrors.email ? 'error' : ''}`}
                      placeholder="your.email@company.com"
                      disabled={formState.isSubmitting}
                      required
                    />
                    {validationErrors.email && (
                      <span className="error-message">
                        <FiAlertCircle />
                        {validationErrors.email}
                      </span>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      <FiPhone className="form-icon" />
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`form-input ${validationErrors.phone ? 'error' : ''}`}
                      placeholder="+1 (555) 123-4567"
                      disabled={formState.isSubmitting}
                      required
                    />
                    {validationErrors.phone && (
                      <span className="error-message">
                        <FiAlertCircle />
                        {validationErrors.phone}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <FiBriefcase className="form-icon" />
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Your company name"
                      disabled={formState.isSubmitting}
                    />
                  </div>
                </div>

                {/* Calendly-style Scheduling */}
                <div className="form-group">
                  <label className="form-label">
                    <FiCalendar className="form-icon" />
                    Select Date & Time *
                  </label>
                  
                  {/* Date Navigation */}
                  <div className="calendar-navigation">
                    <button
                      type="button"
                      className="nav-button"
                      onClick={() => setCurrentWeekStart(Math.max(0, currentWeekStart - 5))}
                      disabled={!canGoPrev() || formState.isSubmitting}
                    >
                      <FiChevronLeft />
                    </button>
                    <span className="nav-title">Available Days</span>
                    <button
                      type="button"
                      className="nav-button"
                      onClick={() => setCurrentWeekStart(currentWeekStart + 5)}
                      disabled={!canGoNext() || formState.isSubmitting}
                    >
                      <FiChevronRight />
                    </button>
                  </div>

                  {/* Date Selection */}
                  <div className="date-grid">
                    {getVisibleDays().map((day) => (
                      <button
                        key={day.dateString}
                        type="button"
                        className={`date-button ${selectedDate?.dateString === day.dateString ? 'selected' : ''}`}
                        onClick={() => handleDateSelect(day)}
                        disabled={formState.isSubmitting}
                      >
                        {day.displayDate}
                      </button>
                    ))}
                  </div>

                  {/* Time Selection */}
                  {selectedDate && (
                    <motion.div 
                      className="time-grid"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="time-grid-header">
                        Available times for {selectedDate.displayDate}
                      </div>
                      <div className="time-slots">
                        {timeSlots.map((slot) => (
                          <button
                            key={slot.value}
                            type="button"
                            className={`time-button ${selectedTime?.value === slot.value ? 'selected' : ''}`}
                            onClick={() => handleTimeSelect(slot)}
                            disabled={formState.isSubmitting}
                          >
                            {slot.display}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}

                  {validationErrors.datetime && (
                    <span className="error-message">
                      <FiAlertCircle />
                      {validationErrors.datetime}
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">
                    <FiMessageSquare className="form-icon" />
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className={`form-textarea ${validationErrors.message ? 'error' : ''}`}
                    placeholder="Tell us about your needs, goals, or any specific questions..."
                    rows="4"
                    disabled={formState.isSubmitting}
                    required
                  />
                  {validationErrors.message && (
                    <span className="error-message">
                      <FiAlertCircle />
                      {validationErrors.message}
                    </span>
                  )}
                </div>

                {formState.error && (
                  <div className="form-error">
                    <FiAlertCircle />
                    {formState.error}
                  </div>
                )}

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={formState.isSubmitting}
                  >
                    {formState.isSubmitting ? (
                      <>
                        <div className="loading-spinner" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FiSend />
                        Schedule Consultation
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ContactModal; 